import DescriptionIcon from '@mui/icons-material/Description';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import SendIcon from '@mui/icons-material/Send';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Link as MuiLink,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';

import { apiErrorMessage } from '@/api/errors';
import { formatMinor } from '@/api/commercial';
import {
  amendQuotation,
  downloadQuotationPdf,
  fetchQuotation,
  issueQuotation,
  overrideQuotationPrice,
  recordQuotationOutcome,
} from '@/api/quotations';
import { PermissionGate } from '@/components/PermissionGate';
import type { QuotationData, QuotationStatus } from '@/types/generated';

/**
 * The quote screen.
 *
 * WHAT THIS PAGE IS FOR is showing a salesperson three numbers at once — what the rules
 * calculated, what the model will eventually recommend, and what they are actually
 * going to charge — and making the gap between them explicit rather than letting an
 * override quietly replace the first with the third.
 *
 * That is also why the line items are read-only. They are the frozen output of the
 * pricing engine, each stamped with the rule version that produced it; an editable grid
 * here would suggest they are working figures, when in fact they are the audit trail. A
 * different price means a new revision, which is what the Reprice action does.
 */

const STATUS_COLOUR: Record<QuotationStatus, 'default' | 'info' | 'success' | 'error' | 'warning'> =
  {
    draft: 'default',
    issued: 'info',
    accepted: 'success',
    rejected: 'error',
    expired: 'warning',
    superseded: 'default',
    withdrawn: 'default',
  };

export function QuotationPage() {
  const { id = '' } = useParams();
  const queryClient = useQueryClient();

  const {
    data: quotation,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['quotation', id],
    queryFn: () => fetchQuotation(id),
    enabled: id !== '',
  });

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error !== null || quotation === undefined) {
    return <Alert severity="error">This quotation could not be loaded.</Alert>;
  }

  // `void`, not the promise: these are fire-and-forget refetches, and returning the promise
  // to a child's callback prop would silently create an unhandled rejection.
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['quotation', id] });
  };

  return (
    <Stack spacing={3}>
      <Header quotation={quotation} onChanged={refresh} />
      <PriceComparison quotation={quotation} onChanged={refresh} />
      <Explanation quotation={quotation} />
      <LineItems quotation={quotation} />
      <CommercialTerms quotation={quotation} onChanged={refresh} />
    </Stack>
  );
}

// --- Header -----------------------------------------------------------------

function Header({ quotation, onChanged }: { quotation: QuotationData; onChanged: () => void }) {
  const [message, setMessage] = useState<string | null>(null);
  const [outcomeDialog, setOutcomeDialog] = useState<'accepted' | 'rejected' | null>(null);

  const issue = useMutation({
    mutationFn: () => issueQuotation(quotation.id),
    onSuccess: () => {
      setMessage('Issued. The document is being generated.');
      onChanged();
    },
  });

  const download = useMutation({
    mutationFn: () => downloadQuotationPdf(quotation.id),
    onSuccess: (blob) => {
      // Null means the API answered 202: the render is queued. Telling the user to try
      // again is honest — the alternative is a broken download and no explanation.
      if (blob === null) {
        setMessage('The document is still being generated. Try again in a moment.');
        return;
      }

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${quotation.reference}-v${quotation.version}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    },
  });

  return (
    <Card sx={{ p: 3 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between">
        <Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="h5">{quotation.label}</Typography>
            <Chip
              size="small"
              label={quotation.status}
              color={STATUS_COLOUR[quotation.status]}
              sx={{ textTransform: 'capitalize' }}
            />
            {quotation.version > 1 && (
              <Tooltip title="Earlier revisions are kept, not overwritten — a customer holding v1 still has a valid document.">
                <Chip size="small" variant="outlined" label={`Revision ${quotation.version}`} />
              </Tooltip>
            )}
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {quotation.customer_name} · from {quotation.rfq_reference}
          </Typography>
          {quotation.has_expired && (
            <Typography variant="body2" color="warning.main" sx={{ mt: 0.5 }}>
              Past its validity date. Reprice before sending it again.
            </Typography>
          )}
        </Box>

        <Stack direction="row" spacing={1} alignItems="flex-start">
          {/* Issuing is gated on `quotation:update`, not a permission of its own: whoever
              builds the quote is who sends it. Only the PRICE is granted separately.

              SHOWN WHILE A DRAFT, DISABLED WHEN THE SERVER WOULD REFUSE. `available_transitions` is
              narrowed by the approval gate, so it is the honest answer to "can this be issued" — and
              `issue_blocked_reason` says why not, on the button itself.

              The button is disabled rather than hidden on purpose: a salesperson looking for how to send
              a quote needs to find the control and be told what is missing. Hiding it would leave them
              searching for something that is not there. */}
          {quotation.status === 'draft' && (
            <PermissionGate permission="quotation:update">
              <Tooltip title={quotation.issue_blocked_reason ?? ''} placement="bottom">
                <span>
                  <Button
                    variant="contained"
                    startIcon={<SendIcon />}
                    disabled={
                      issue.isPending || !quotation.available_transitions.includes('issued')
                    }
                    onClick={() => issue.mutate()}
                  >
                    Issue
                  </Button>
                </span>
              </Tooltip>
            </PermissionGate>
          )}

          {quotation.status === 'issued' && (
            <PermissionGate permission="quotation:update">
              <Button variant="contained" onClick={() => setOutcomeDialog('accepted')}>
                Won
              </Button>
              <Button variant="outlined" color="error" onClick={() => setOutcomeDialog('rejected')}>
                Lost
              </Button>
            </PermissionGate>
          )}

          {/* `warrantsDocument()` on the server is "anything except draft and withdrawn", and this used
              to be the looser "not draft" — so a withdrawn quote offered a PDF that does not exist. It was
              also the one action here with no permission gate, while the endpoint checks
              `quotation:download`. */}
          {quotation.status !== 'draft' && quotation.status !== 'withdrawn' && (
            <PermissionGate permission="quotation:download">
              <Button
                variant="outlined"
                startIcon={<DescriptionIcon />}
                disabled={download.isPending}
                onClick={() => download.mutate()}
              >
                PDF
              </Button>
            </PermissionGate>
          )}
        </Stack>
      </Stack>

      {message !== null && (
        <Alert severity="info" sx={{ mt: 2 }} onClose={() => setMessage(null)}>
          {message}
        </Alert>
      )}
      {/* Stated on the page, not only in the button's tooltip. This is the sentence that replaces the
          error the user used to get, and it is here BEFORE anybody clicks anything. */}
      {quotation.issue_blocked_reason !== null && quotation.status === 'draft' && (
        <Alert severity="warning" sx={{ mt: 2 }} icon={<HourglassEmptyIcon fontSize="inherit" />}>
          {quotation.issue_blocked_reason}
          {quotation.is_awaiting_approval && (
            <>
              {' '}
              <MuiLink component={RouterLink} to="/approvals">
                See the approvals queue
              </MuiLink>
              .
            </>
          )}
        </Alert>
      )}

      {issue.isError && (
        <Alert severity="error" sx={{ mt: 2 }} onClose={() => issue.reset()}>
          {apiErrorMessage(issue.error)}
        </Alert>
      )}

      <OutcomeDialog
        quotation={quotation}
        outcome={outcomeDialog}
        onClose={() => setOutcomeDialog(null)}
        onRecorded={onChanged}
      />
    </Card>
  );
}

/**
 * Recording the answer.
 *
 * A rejection REQUIRES a reason, which is why this is a dialog rather than two buttons
 * that fire immediately. "Lost on price" and "lost on transit time" are different facts,
 * and the difference is the whole value of the label.
 */
function OutcomeDialog({
  quotation,
  outcome,
  onClose,
  onRecorded,
}: {
  quotation: QuotationData;
  outcome: 'accepted' | 'rejected' | null;
  onClose: () => void;
  onRecorded: () => void;
}) {
  const [reason, setReason] = useState('');

  const record = useMutation({
    mutationFn: () => recordQuotationOutcome(quotation.id, outcome ?? 'accepted', reason),
    onSuccess: () => {
      setReason('');
      onClose();
      onRecorded();
    },
  });

  const isRejection = outcome === 'rejected';

  return (
    <Dialog open={outcome !== null} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{isRejection ? 'Record a loss' : 'Record a win'}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          {isRejection
            ? 'Why did this quote lose? This becomes training data, so specifics matter more than sentiment.'
            : `Accepting marks ${quotation.rfq_reference} as won.`}
        </DialogContentText>
        {isRejection && (
          <TextField
            fullWidth
            multiline
            minRows={2}
            required
            label="Reason"
            placeholder="e.g. 8% above the incumbent's rate on this lane"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
        )}
        {record.isError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {apiErrorMessage(record.error)}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          color={isRejection ? 'error' : 'primary'}
          disabled={record.isPending || (isRejection && reason.trim() === '')}
          onClick={() => record.mutate()}
        >
          Record
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// --- The three prices --------------------------------------------------------

/**
 * THE POINT OF THE PRODUCT, on one card.
 *
 * The calculated total stays visible after an override precisely so the delta is
 * legible. Overwriting it would be the obvious simplification and would destroy the
 * comparison the whole Phase 3 story rests on.
 */
function PriceComparison({
  quotation,
  onChanged,
}: {
  quotation: QuotationData;
  onChanged: () => void;
}) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [costOpen, setCostOpen] = useState(false);

  const clear = useMutation({
    mutationFn: () => overrideQuotationPrice(quotation.id, null),
    onSuccess: onChanged,
  });

  return (
    <Card sx={{ p: 3 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={4}
        divider={<Divider orientation="vertical" flexItem />}
      >
        <Figure
          label="Rules engine"
          value={formatMinor(quotation.calculated_total_minor, quotation.currency)}
          hint="Reproducible from the rule versions on each line."
        />

        <Figure
          label="AI recommendation"
          value={
            quotation.ai_price_minor === null
              ? '—'
              : formatMinor(quotation.ai_price_minor, quotation.currency)
          }
          hint={
            quotation.ai_price_minor === null
              ? // A dash rather than a spinner or an error. No recommendation is an ordinary
                // state — no model promoted, the service restarting, a lane the model refused —
                // and none of them is the salesperson's problem.
                'No recommendation for this quote.'
              : `${signed(quotation.ai_delta_basis_points)} against the final price.`
          }
          band={
            quotation.ai_confidence_low_minor !== null &&
            quotation.ai_confidence_high_minor !== null
              ? `${formatMinor(quotation.ai_confidence_low_minor, quotation.currency)} – ${formatMinor(
                  quotation.ai_confidence_high_minor,
                  quotation.currency,
                )}`
              : undefined
          }
        />

        <Figure
          label="Quoted to the customer"
          value={formatMinor(quotation.final_price_minor, quotation.currency)}
          emphasis
          hint={
            quotation.manual_price_minor === null
              ? 'The calculated price, unchanged.'
              : `Overridden ${signed(quotation.override_delta_basis_points)}.`
          }
        />

        {/* BEFORE MARGIN, BECAUSE MARGIN IS DERIVED FROM IT. Reading the four prices and then being told
            a margin is Unknown, with the reason on a different card further down the page, made the one
            number a salesperson is judged on look like a defect rather than a missing input. */}
        <Figure
          label="Estimated cost"
          value={
            quotation.estimated_cost_minor === null
              ? 'Not recorded'
              : formatMinor(quotation.estimated_cost_minor, quotation.currency)
          }
          hint={
            quotation.cost_breakdown !== null
              ? `Built from ${quotation.cost_breakdown.components.length} cost lines. Internal only.`
              : quotation.estimated_cost_minor === null
                ? // Nothing computes a cost — the engine prices what we charge — so this is a prompt for
                  // a person rather than a fault.
                  'Nothing computes this. Record it to get a margin.'
                : 'Entered as a single figure. Internal only.'
          }
        />

        <Figure
          label="Margin"
          value={
            quotation.margin_basis_points === null
              ? 'Unknown'
              : `${(quotation.margin_basis_points / 100).toFixed(2)}%`
          }
          hint={
            quotation.margin_basis_points === null
              ? // Null rather than zero, because a margin of zero is a claim.
                'Needs an estimated cost.'
              : 'Against the final price, not the calculated one.'
          }
        />
      </Stack>

      {/* The lines behind the figure, when it was built up rather than typed.

          READ ONLY IN THIS APP, DELIBERATELY. Building a cost up is an admin-panel job — a repeater with
          standing rates loaded from the organisation's cost variables — and a second full editor here
          would double the surface for the same feature. What a salesperson needs is to SEE what the
          margin they are judged on was computed from. */}
      {quotation.cost_breakdown !== null && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="overline" color="text.secondary">
            Cost build-up
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            Internal only. Not shown on the quotation.
          </Typography>

          <Table size="small">
            <TableBody>
              {quotation.cost_breakdown.components.map((component) => (
                <TableRow key={component.label}>
                  <TableCell sx={{ pl: 0 }}>{component.label}</TableCell>
                  {/* The arithmetic, so the figure can be checked without opening the admin panel. */}
                  <TableCell sx={{ color: 'text.secondary' }}>{component.basis ?? '—'}</TableCell>
                  <TableCell align="right">
                    {formatMinor(component.amount_minor, quotation.currency)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      {quotation.override_reason !== null && (
        <Alert severity="info" sx={{ mt: 2 }}>
          <strong>Override reason:</strong> {quotation.override_reason}
        </Alert>
      )}

      {quotation.is_editable && (
        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
          {/* Moved here from the Terms card. Cost belongs beside the prices it is compared against, and
              having it on a separate panel is why "Margin: Unknown" read as unexplained. */}
          <PermissionGate permission="quotation:update">
            <Button variant="outlined" onClick={() => setCostOpen(true)}>
              {quotation.estimated_cost_minor === null ? 'Record the cost' : 'Change the cost'}
            </Button>
          </PermissionGate>

          <PermissionGate permission="quotation:override-price">
            <Button variant="outlined" onClick={() => setDialogOpen(true)}>
              {quotation.manual_price_minor === null ? 'Override the price' : 'Change the override'}
            </Button>
            {quotation.manual_price_minor !== null && (
              <Button color="inherit" disabled={clear.isPending} onClick={() => clear.mutate()}>
                Revert to calculated
              </Button>
            )}
          </PermissionGate>
        </Stack>
      )}

      <OverrideDialog
        quotation={quotation}
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSaved={onChanged}
      />

      <EstimatedCostDialog
        quotation={quotation}
        open={costOpen}
        onClose={() => setCostOpen(false)}
        onSaved={onChanged}
      />
    </Card>
  );
}

function Figure({
  label,
  value,
  hint,
  band,
  emphasis = false,
}: {
  label: string;
  value: string;
  hint: string;
  band?: string | undefined;
  emphasis?: boolean;
}) {
  return (
    <Box sx={{ minWidth: 160 }}>
      <Typography variant="overline" color="text.secondary">
        {label}
      </Typography>
      <Typography variant={emphasis ? 'h5' : 'h6'} sx={{ fontWeight: emphasis ? 600 : 400 }}>
        {value}
      </Typography>
      {band !== undefined && (
        // The range, directly under the point estimate. A single number implies a precision the
        // model does not have, and M10 measured this band's coverage below its nominal level —
        // so showing it is the honest presentation, not decoration.
        <Typography variant="caption" color="text.secondary" display="block">
          likely {band}
        </Typography>
      )}
      <Typography variant="caption" color="text.secondary">
        {hint}
      </Typography>
    </Box>
  );
}

/**
 * The override, with its mandatory reason.
 *
 * The reason field is `required` here and enforced by the API, a domain guard, and a
 * database CHECK constraint. That is not redundancy for its own sake: a price a human
 * changed without saying why is exactly the record an audit asks about first.
 */
/**
 * Record a single estimated cost.
 *
 * NOTHING COMPUTES A COST IN THIS SYSTEM. The engine prices what a customer is charged; what a job costs
 * is operator-supplied, which is why margin is null rather than zero until somebody says. This is the
 * one-figure route — a build-up of named lines from the organisation's standing rates is an admin-panel
 * job, and this dialog says so rather than pretending the two are equivalent.
 *
 * CLEARING IS ALLOWED AND RETURNS MARGIN TO UNKNOWN, because a cost recorded in error should be
 * removable and an absence is the honest answer when nobody knows.
 */
function EstimatedCostDialog({
  quotation,
  open,
  onClose,
  onSaved,
}: {
  quotation: QuotationData;
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [major, setMajor] = useState(
    quotation.estimated_cost_minor === null ? '' : String(quotation.estimated_cost_minor / 100),
  );

  const save = useMutation({
    mutationFn: () =>
      amendQuotation(quotation.id, {
        estimated_cost_minor: major === '' ? null : Math.round(Number(major) * 100),
      }),
    onSuccess: () => {
      onClose();
      onSaved();
    },
  });

  const parsed = Number(major);
  const margin =
    major !== '' && Number.isFinite(parsed) && quotation.final_price_minor > 0
      ? ((quotation.final_price_minor - Math.round(parsed * 100)) * 100) /
        quotation.final_price_minor
      : null;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Estimated cost</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Internal only — never shown to the customer. Margin is computed from this and cannot be
          computed without it.
        </DialogContentText>

        <TextField
          fullWidth
          type="number"
          label={`Estimated cost (${quotation.currency})`}
          value={major}
          onChange={(event) => setMajor(event.target.value)}
          helperText={
            margin === null
              ? 'Leave blank to clear the cost and return the margin to unknown.'
              : // Shown live, because a figure entered a hundred times too large is otherwise invisible
                // until it has been in the reports for a quarter.
                `Quoted ${formatMinor(quotation.final_price_minor, quotation.currency)}. Margin would be ${margin.toFixed(2)}%.`
          }
        />

        {quotation.cost_breakdown !== null && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            This quotation has a cost build-up of {quotation.cost_breakdown.components.length}{' '}
            lines. Saving a single figure here replaces it — the lines and their arithmetic are
            discarded.
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={save.isPending} onClick={() => save.mutate()}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function OverrideDialog({
  quotation,
  open,
  onClose,
  onSaved,
}: {
  quotation: QuotationData;
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [major, setMajor] = useState('');
  const [reason, setReason] = useState('');

  const save = useMutation({
    mutationFn: () => overrideQuotationPrice(quotation.id, Math.round(Number(major) * 100), reason),
    onSuccess: () => {
      setMajor('');
      setReason('');
      onClose();
      onSaved();
    },
  });

  const parsed = Number(major);
  const preview =
    major !== '' && Number.isFinite(parsed) && quotation.calculated_total_minor > 0
      ? Math.round(
          ((Math.round(parsed * 100) - quotation.calculated_total_minor) * 10_000) /
            quotation.calculated_total_minor,
        )
      : null;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Override the price</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          The calculated total is kept. What you enter becomes the quoted price, and the difference
          between the two is recorded.
        </DialogContentText>
        <Stack spacing={2}>
          <TextField
            fullWidth
            required
            type="number"
            label={`Quoted price (${quotation.currency})`}
            value={major}
            onChange={(event) => setMajor(event.target.value)}
            helperText={
              preview === null
                ? `Calculated: ${formatMinor(quotation.calculated_total_minor, quotation.currency)}`
                : `${signed(preview)} against the calculated price.`
            }
          />
          <TextField
            fullWidth
            required
            multiline
            minRows={2}
            label="Reason"
            placeholder="e.g. matched the incumbent to keep the Mombasa–Kampala corridor"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            helperText="Required. This is the signal the pricing model learns from."
          />
        </Stack>
        {save.isError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {apiErrorMessage(save.error)}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={save.isPending || major === '' || reason.trim() === ''}
          onClick={() => save.mutate()}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// --- Why the model said that -------------------------------------------------

/**
 * The SHAP contribution waterfall.
 *
 * A RECOMMENDATION NOBODY CAN INTERROGATE IS A RECOMMENDATION NOBODY USES. A salesperson asked to
 * quote 8% above the rate card will not do it because a box said so; they will do it if the box
 * says the lane and the cargo type are what moved it, because that is an argument they can take
 * to a customer — or reject.
 *
 * Bars are scaled against the LARGEST contribution rather than against the price. Contributions
 * are frequently a few percent of the total, and scaling against the price would render every one
 * of them as a line too short to read.
 *
 * Renders nothing at all when there is no explanation. An empty chart with a title is worse than
 * an absent one: it implies something failed, when the ordinary case is simply that no model is
 * promoted.
 */
function Explanation({ quotation }: { quotation: QuotationData }) {
  const contributions = quotation.ai_contributions;

  if (contributions.length === 0 || quotation.ai_price_minor === null) {
    return null;
  }

  const largest = Math.max(...contributions.map((c) => Math.abs(c.impact)), 1);

  return (
    <Card sx={{ p: 3 }}>
      <Typography variant="h6">Why the model suggests that price</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        The features that moved the recommendation furthest, from model {quotation.ai_model_version}
        . Positive pushed the price up.
      </Typography>

      <Stack spacing={1.5}>
        {contributions.map((contribution) => {
          const share = Math.abs(contribution.impact) / largest;
          const positive = contribution.impact >= 0;

          return (
            <Box key={contribution.feature}>
              <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                <Typography variant="body2">
                  {contribution.description ?? contribution.feature}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 500 }}
                  color={positive ? 'error.main' : 'success.main'}
                >
                  {positive ? '+' : '−'}
                  {formatMinor(Math.abs(contribution.impact), quotation.currency)}
                </Typography>
              </Stack>

              {/* WHAT THE FEATURE MEANS, not what it is. The line above is the feature and its
                  value; this is why that would move a price at all — the sentence a salesperson
                  repeats to the customer who asks. Omitted entirely when there is no copy for a
                  feature: a missing explanation is invisible, a guessed one gets said out loud. */}
              {contribution.meaning && (
                <Typography variant="caption" color="text.secondary" display="block">
                  {contribution.meaning}
                </Typography>
              )}

              {/* A plain div rather than a charting library. Six horizontal bars do not justify
                  200 kB of dependency, and this renders identically in a PDF later. */}
              <Box
                sx={{
                  mt: 0.5,
                  height: 6,
                  borderRadius: 3,
                  width: `${Math.max(share * 100, 2)}%`,
                  bgcolor: positive ? 'error.light' : 'success.light',
                }}
              />
            </Box>
          );
        })}
      </Stack>

      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
        The rules engine remains the system of record. This is advice, and the price you send is the
        one you decide.
      </Typography>
    </Card>
  );
}

// --- The frozen lines --------------------------------------------------------

function LineItems({ quotation }: { quotation: QuotationData }) {
  const lines = quotation.line_items ?? [];

  return (
    <Card sx={{ p: 3 }}>
      <Typography variant="h6">How this price was reached</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Read-only. Each line is the frozen output of a specific rule version, so this quote still
        explains itself after the rate card changes.
      </Typography>

      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Charge</TableCell>
              <TableCell>Rule</TableCell>
              <TableCell>Basis</TableCell>
              <TableCell align="right">Amount</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {lines.map((line) => (
              <TableRow key={line.id}>
                <TableCell>
                  {line.description}
                  {line.minimum_applied && (
                    <Tooltip title="A minimum charge floor was applied, so this is not the raw rate.">
                      <Chip size="small" label="min" sx={{ ml: 1 }} />
                    </Tooltip>
                  )}
                  {line.maximum_applied && (
                    <Tooltip title="A cap was applied.">
                      <Chip size="small" label="cap" sx={{ ml: 1 }} />
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell>
                  {line.is_manual ? (
                    <Chip size="small" variant="outlined" label="manual" />
                  ) : (
                    <Tooltip
                      title={`Rule version ${line.pricing_rule_version_id ?? 'unknown'} — the audit trail for this line.`}
                    >
                      <span>{line.rule_code ?? '—'}</span>
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {line.basis}
                  </Typography>
                </TableCell>
                <TableCell align="right">{formatMinor(line.amount_minor, line.currency)}</TableCell>
              </TableRow>
            ))}
            {lines.length === 0 && (
              <TableRow>
                <TableCell colSpan={4}>
                  <Typography variant="body2" color="text.secondary">
                    No lines. This quotation cannot be issued until it has been priced.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>

      <Divider sx={{ my: 2 }} />

      <Stack spacing={0.5} sx={{ maxWidth: 320, ml: 'auto' }}>
        <Total
          label="Subtotal"
          value={formatMinor(quotation.calculated_subtotal_minor, quotation.currency)}
        />
        {quotation.calculated_tax_minor > 0 && (
          <Total
            label="Tax"
            value={formatMinor(quotation.calculated_tax_minor, quotation.currency)}
          />
        )}
        <Total
          label="Calculated total"
          value={formatMinor(quotation.calculated_total_minor, quotation.currency)}
        />
        {quotation.manual_price_minor !== null && (
          <Total
            label="Agreed price"
            value={formatMinor(quotation.final_price_minor, quotation.currency)}
            emphasis
          />
        )}
      </Stack>
    </Card>
  );
}

function Total({
  label,
  value,
  emphasis = false,
}: {
  label: string;
  value: string;
  emphasis?: boolean;
}) {
  return (
    <Stack direction="row" justifyContent="space-between">
      <Typography variant="body2" sx={{ fontWeight: emphasis ? 600 : 400 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: emphasis ? 600 : 400 }}>
        {value}
      </Typography>
    </Stack>
  );
}

// --- Terms -------------------------------------------------------------------

function CommercialTerms({
  quotation,
  onChanged,
}: {
  quotation: QuotationData;
  onChanged: () => void;
}) {
  const [terms, setTerms] = useState(quotation.terms ?? '');
  const [notes, setNotes] = useState(quotation.notes ?? '');

  // COST IS NO LONGER SENT FROM HERE, and that is a fix as well as a move. This panel used to include
  // `estimated_cost_minor` in every save whether or not it had been touched — and because a single figure
  // replaces a cost build-up, editing a comma in Terms would silently discard six cost lines. Cost now
  // lives beside the prices it is compared against, and this request carries only what it is about.
  const save = useMutation({
    mutationFn: () => amendQuotation(quotation.id, { terms, notes }),
    onSuccess: onChanged,
  });

  if (!quotation.is_editable) {
    return (
      <Card sx={{ p: 3 }}>
        <Typography variant="h6">Terms</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {quotation.terms ?? 'No terms recorded.'}
        </Typography>
        {quotation.valid_until !== null && (
          <Typography variant="body2" sx={{ mt: 1 }}>
            Valid until {quotation.valid_until}
          </Typography>
        )}
      </Card>
    );
  }

  return (
    <Card sx={{ p: 3 }}>
      <Typography variant="h6">Terms</Typography>
      <Stack spacing={2} sx={{ mt: 2 }}>
        <TextField
          fullWidth
          multiline
          minRows={2}
          label="Terms"
          value={terms}
          onChange={(event) => setTerms(event.target.value)}
        />
        <TextField
          fullWidth
          multiline
          minRows={2}
          label="Notes"
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
        />
        <Box>
          <Button variant="contained" disabled={save.isPending} onClick={() => save.mutate()}>
            Save
          </Button>
        </Box>
        {save.isError && <Alert severity="error">{apiErrorMessage(save.error)}</Alert>}
      </Stack>
    </Card>
  );
}

// --- Helpers -----------------------------------------------------------------

/** Basis points as a signed percentage: −10.00% reads better than −1000 bp to a salesperson. */
function signed(basisPoints: number | null): string {
  if (basisPoints === null) {
    return '—';
  }

  const percent = (basisPoints / 100).toFixed(2);

  return basisPoints > 0 ? `+${percent}%` : `${percent}%`;
}
